import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { Tool } from './Tool';

import { ApiService } from 'js/src/client/services.gen';
import {
    getTemplateBundle,
    getTemplateDefaultVoiceoverSceneDesign,
    getVoiceoverSceneDesignsForTemplate,
} from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { withDataLuminarySync } from 'js/src/luminary';

/**
 * Finds the current template's audiogram scene design programmatically, by
 * instantiating each voiceover design in a data luminary and checking for a
 * voice-reactive waveform icon (playbackMode 'waveform') — the same probe
 * clip creator uses. This exists so prompts never have to hardcode scene
 * design names, which are a per-template convention, not a contract.
 */
export const findWaveformSceneDesignSchema = z.object({});
export type FindWaveformSceneDesignArgs = z.infer<typeof findWaveformSceneDesignSchema>;

export class FindWaveformSceneDesignTool extends Tool<typeof findWaveformSceneDesignSchema> {
    protected override readonly schema = findWaveformSceneDesignSchema;
    protected override readonly toolName = TOOL_NAMES.findWaveformSceneDesign;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = `Find the current template's audiogram scene design — the voiceover scene design containing a waveform that pulses with the voice. Returns the scene design id to pass to ${TOOL_NAMES.addVoiceoverScene} with \`cover_whole_group: true\` for a full-video audiogram. Waveforms never appear in scene design annotations, so use this instead of guessing from ${TOOL_NAMES.listSceneDesigns}.`;

    public override getToolDisplayName(): string {
        return 'Finding waveform scene design';
    }

    public get statusIcon(): string {
        return 'manage_search';
    }

    public async execute(_args: FindWaveformSceneDesignArgs, _context: ToolExecutionContext) {
        const templateId = this.luminary.template.id;
        if (!templateId) {
            return 'Error: No template is loaded, so there are no scene designs to search.';
        }

        try {
            const state = (this.getState as () => RootState)();
            const voiceoverSceneDesigns = getVoiceoverSceneDesignsForTemplate(state, templateId);
            const templateBundle = getTemplateBundle(state, templateId);
            if (!templateBundle) {
                return `Error: Template "${templateId}" is not loaded yet. Try again once the video has finished loading.`;
            }
            const defaultVoiceoverSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
                state,
                templateId,
            );

            const matches = withDataLuminarySync(
                (dataLuminary) => {
                    const found: string[] = [];
                    for (const { id: sceneDesignId } of voiceoverSceneDesigns) {
                        // The default voiceover design is the base scene; it can't
                        // be used as an overlay even if it contains a waveform.
                        if (sceneDesignId === defaultVoiceoverSceneDesignId) {
                            continue;
                        }
                        const scene = dataLuminary.addScene({ sceneDesignId });
                        const hasWaveform = scene
                            .getAssetsByType(VideoData.ICON_ASSET_TYPE)
                            .some(
                                (iconAsset) =>
                                    iconAsset.playbackMode === VideoData.PLAYBACK_WAVEFORM,
                            );
                        if (hasWaveform) {
                            found.push(sceneDesignId);
                        }
                    }
                    return found;
                },
                { templateBundle },
            );

            if (matches.length === 0) {
                return await this.formatNoWaveformError(templateId);
            }
            const others =
                matches.length > 1 ? ` Other matches: ${matches.slice(1).join(', ')}.` : '';
            return `Success: The waveform (audiogram) scene design is "${matches[0]}".${others}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error finding waveform scene design: ${errorMessage}`;
        }
    }

    /**
     * Builds the failure message when the current template has no waveform design,
     * enriching it with templates that DO support the audiogram style so the agent
     * can switch rather than get stuck. Falls back to the plain error if the
     * suggestion endpoint is unavailable.
     */
    private async formatNoWaveformError(templateId: string): Promise<string> {
        const baseError = `Error: Template "${templateId}" has no waveform (audiogram) scene design, so this template doesn't support the audiogram style.`;
        try {
            const response = await ApiService.templatesWaveformCapableRetrieve();
            const suggestions = response.data.template_ids.filter((id) => id !== templateId);
            if (suggestions.length === 0) {
                return baseError;
            }
            return `${baseError} Templates that DO support it: ${suggestions.join(
                ', ',
            )}. Switch to one with ${TOOL_NAMES.rebuildVideo}, then try ${TOOL_NAMES.findWaveformSceneDesign} again.`;
        } catch {
            return baseError;
        }
    }
}
